import { useCallback } from 'react';
import { usePreferencesContext } from '../../core/providers/PreferencesProvider';
import { updateLanguageFromPreferences } from '../../locales';

/**
 * Hook pour gérer les préférences utilisateur
 * @returns {Object} Interface de gestion des préférences
 */
export const usePreferences = () => {
  const {
    preferences,
    isLoading,
    updatePreference,
    updatePreferences,
    resetPreferences
  } = usePreferencesContext();

  /**
   * Récupère une préférence spécifique
   * @param {string} key - Clé de la préférence (peut être imbriquée avec '.')
   * @returns {any} Valeur de la préférence
   */
  const getPreference = useCallback((key) => {
    if (!key) return preferences;
    
    // Gestion des clés imbriquées (ex: notificationTypes.newMessage)
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      return preferences[parent]?.[child];
    }
    
    return preferences[key];
  }, [preferences]);

  /**
   * Vérifie si une préférence est activée
   * @param {string} key - Clé de la préférence
   * @returns {boolean}
   */
  const isEnabled = useCallback((key) => {
    return Boolean(getPreference(key));
  }, [getPreference]);

  /**
   * Met à jour une préférence avec validation de type
   * @param {string} key - Clé de la préférence
   * @param {any} value - Nouvelle valeur
   */
  const set = useCallback(async (key, value) => {
    try {
      // Mettre à jour la préférence via le contexte
      await updatePreference(key, value);
      
      // Si la préférence mise à jour est la langue, mettre à jour i18n
      if (key === 'language') {
        updateLanguageFromPreferences({ language: value });
      }
      
      return true;
    } catch (error) {
      console.error(`Error updating preference ${key}:`, error);
      return false;
    }
  }, [updatePreference]);

  /**
   * Met à jour plusieurs préférences à la fois
   * @param {Object} updates - Objet contenant les mises à jour
   */
  const setMultiple = useCallback((updates) => {
    updatePreferences(updates);
  }, [updatePreferences]);

  /**
   * Réinitialise toutes les préférences
   */
  const reset = useCallback(() => {
    resetPreferences();
  }, [resetPreferences]);

  return {
    // État
    preferences,
    isLoading,
    
    // Getters
    get: getPreference,
    isEnabled,
    
    // Setters
    set,
    setMultiple,
    reset,
    
    // Valeurs spécifiques couramment utilisées
    language: preferences.language,
    theme: preferences.theme,
    notificationTypes: preferences.notificationTypes || {},
    location: preferences.location
  };
}; 